import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Bell,
  Menu,
  User,
  ChevronDown,
  HelpCircle,
  LogOut,
  Lock,
} from "lucide-react";
import { useAdminAuth } from "../../services/admin/AdminAuthServices";
import { useComingSoon } from "../../utils/useComingSoon";


export default function AdminHeader({ onMenuClick }) {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { admin, adminLogout } = useAdminAuth();
  const { showComingSoon } = useComingSoon();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      setDropdownOpen(false);
      await adminLogout(navigate); // ✅ backend logout + cleanup + redirect handled inside
    } catch (error) {
      console.error("Logout failed:", error);
      localStorage.removeItem("admin");
      localStorage.removeItem("admin_token");
      navigate("/admin/login", { replace: true });
    }
  };

  return (
    <header className="sticky top-0 z-30 bg-white dark:bg-[#1A1A1A] border-b border-[#E5E7EB] dark:border-[#2A2A2A] px-4 lg:px-6 py-4 flex items-center justify-between">
      {/* Mobile & Tablet Menu Button */}
      <button
        onClick={onMenuClick}
        className="lg:hidden p-2 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors flex-shrink-0"
      >
        <Menu size={24} className="text-[#111111] dark:text-white" />
      </button>

      {/* Logo - Medium and Small Screens Only */}
      <div className="lg:hidden flex items-center gap-2 ml-2 flex-shrink-0">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center shadow-md border border-jazz-brown/20">
          <img src="/static/images/temb_music_logo_transparent.png" alt="Temidayo Balogun Logo" loading="eager" />
        </div>
        <div className="hidden md:block">
          <h2 className="font-bold text-[#111111] dark:text-white text-xs font-poppins leading-tight">
            St. Stephen's
          </h2>
          <p className="text-xs text-black dark:text-[#A0A0A0] font-inter leading-tight">
            Rentals
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex flex-1 max-w-xl mx-2 md:mx-4">
        <div className="relative w-full">
          <Search
            size={18}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black dark:text-[#A0A0A0]"
          />
          <input
            type="text"
            placeholder="Search..."
            className="w-full pl-9 pr-3 py-2 md:py-2.5 md:pl-10 md:pr-4 rounded-xl border border-[#E5E7EB] dark:border-[#2A2A2A] bg-[#F9FAFB] dark:bg-[#262626] text-black dark:text-white placeholder-black/70 dark:placeholder-[#A0A0A0] focus:outline-none focus:ring-2 focus:ring-jazz-brown dark:focus:ring-jazz-brown/50 transition-all text-sm"
            onClick={() =>
              showComingSoon({
                title: "Search Coming Soon!",
                message: "We're building the search feature. Stay tuned!",
              })
            }
          />
        </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
        {/* Notifications */}
        <button
          className="relative p-2 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
          onClick={() =>
            showComingSoon({
              title: "Notifications Coming Soon!",
              message: "We're building the notifications feature. Stay tuned!",
            })
          }
        >
          <Bell size={20} className="text-black dark:text-[#A0A0A0]" />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        </button>

        {/* User Menu with Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
          >
            <div className="w-10 h-10 bg-black border border-jazz-brown/30 rounded-full flex items-center justify-center">
              <User size={18} className="text-white" />
            </div>
            <div className="hidden lg:flex flex-col items-start">
              <span className="text-sm font-semibold text-[#111111] dark:text-white">
                {admin?.full_name || "Admin User"}
              </span>
              <span className="text-xs text-black dark:text-[#A0A0A0]">
                {admin?.role
                  ? admin.role.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())
                  : "Super Admin"}
              </span>
            </div>
            <ChevronDown
              size={16}
              className="hidden lg:block text-black dark:text-[#A0A0A0]"
            />
          </button>

          {/* Dropdown Menu */}
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl shadow-lg overflow-hidden z-50">
              {/* User Info in Dropdown - Mobile */}
              <div className="lg:hidden px-4 py-3 border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
                <p className="text-sm font-semibold text-[#111111] dark:text-white">
                  {admin?.full_name || "Admin User"}
                </p>
                <p className="text-xs text-black dark:text-[#A0A0A0] mt-1">
                  {admin?.role
                    ? admin.role.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())
                    : "Super Admin"}
                </p>
              </div>

              {/* Menu Items */}
              <div className="py-2">
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#111111] dark:text-white hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
                  onClick={() =>
                    showComingSoon({
                      title: "Profile Coming Soon!",
                      message: "We're building the profile feature. Stay tuned!",
                    })
                  }
                >
                  <User
                    size={16}
                    className="text-black dark:text-[#A0A0A0]"
                  />
                  My Profile
                </button>
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#111111] dark:text-white hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
                  onClick={() =>
                    showComingSoon({
                      title: "Help & Support Coming Soon!",
                      message:
                        "We're building the help & support feature. Stay tuned!",
                    })
                  }
                >
                  <HelpCircle
                    size={16}
                    className="text-black dark:text-[#A0A0A0]"
                  />
                  Help & Support
                </button>
                <button
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#111111] dark:text-white hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
                  onClick={() => {
                    setDropdownOpen(false);
                    navigate("/admin/change-password");
                  }}
                >
                  <Lock
                    size={16}
                    className="text-black dark:text-[#A0A0A0]"
                  />
                  Change Password
                </button>
              </div>

              {/* Logout */}
              <div className="border-t border-[#E5E7EB] dark:border-[#2A2A2A] py-2">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
